import React, { FC, useCallback, useRef } from 'react';

import {
	BUTTON_TYPES,
	EvPopup,
	EvSelectNew,
	EvTag,
	OptionType,
	TAG_STATUS,
	TITLE_MODES
} from '@evinced-private/ui-common';

import { RemoveIconButton } from 'src/apps/web-sdks/pages/tests/components/RemoveIconButton';
import { IWebSdkTest } from 'src/apps/web-sdks/types/WebSdkDashboardServiceTypes';
import { PLATFORM_APP_ID } from 'src/common/consts/dom-consts';

import { RemoveLabelTestsModalProps } from '../shared/types';

import { useLabelsRemoval } from './hooks/useLabelsRemoval';
import {
	CANCEL_BUTTON_TEXT,
	getModalTitle,
	MODAL_DESCRIPTION,
	SAVE_BUTTON_TEXT
} from './constants';
import { handleSave } from './handlers';

import './RemoveLabelTestsModal.scss';

export const RemoveLabelTestsModal: FC<RemoveLabelTestsModalProps> = ({
	isOpen,
	onClose,
	testRows,
	onSaveSuccess
}) => {
	const { selectedLabel, setSelectedLabel, removals, labelOptions, removeRemoval, resetRemovals } =
		useLabelsRemoval({ testRows });

	const ariaLiveRef = useRef<HTMLDivElement>(null);

	const handleCloseAction = (): void => {
		resetRemovals();
		onClose();
	};

	const handleRemoveRemoval = useCallback(
		(index: number, label: string) => {
			removeRemoval(index);
			// Announce removal
			if (ariaLiveRef.current) {
				ariaLiveRef.current.textContent = `${label} removed from removal list`;
			}
		},
		[removeRemoval]
	);

	const handleLabelsChange = useCallback(
		(values: OptionType[]) => {
			setSelectedLabel(values);
			// Announce addition
			if (values.length > 0 && ariaLiveRef.current) {
				ariaLiveRef.current.textContent = `${values[0].label} added to removal list`;
			}
		},
		[setSelectedLabel]
	);

	const handleSaveAction = async (): Promise<void> => {
		await handleSave(removals, testRows as IWebSdkTest[], onClose, onSaveSuccess);
		resetRemovals();
	};

	const buttons = [
		{
			title: CANCEL_BUTTON_TEXT,
			onClick: handleCloseAction,
			type: BUTTON_TYPES.GHOST
		},
		{
			title: SAVE_BUTTON_TEXT,
			onClick: handleSaveAction
		}
	];

	return (
		<EvPopup
			appElement={PLATFORM_APP_ID}
			isOpen={isOpen}
			onClose={handleCloseAction}
			popupWidth="600px"
			title={getModalTitle(testRows)}
			titleMode={TITLE_MODES.MAIN}
			buttons={buttons}
			isCentered={true}
			isControlled
			isBgTransparent={true}
		>
			<div ref={ariaLiveRef} aria-live="polite" aria-atomic="true" className="sr-only" />
			<div className="remove-label-tests-modal__content">
				<p
					id="remove-label-tests-modal-description"
					className="remove-label-tests-modal__description"
				>
					{MODAL_DESCRIPTION}
				</p>

				<div className="remove-label-tests-modal__select-wrapper">
					<EvSelectNew
						id="labels-select"
						label="Label"
						options={labelOptions}
						selectedValue={selectedLabel}
						onSelectChange={handleLabelsChange}
						placeholder="Select label"
						listboxWidth="100%"
						showTooltip={true}
						tooltipContent="Pick a label; it will be added below"
						shouldSortOptions={true}
					/>
				</div>

				{removals.length > 0 && (
					<>
						<div className="remove-label-tests-modal__divider" />

						<div className="remove-label-tests-modal__removals-section">
							<h3 className="remove-label-tests-modal__removals-title">Labels to remove</h3>
							<div className="remove-label-tests-modal__removals-list">
								{removals.map((removal, index) => (
									<div key={removal.label.value} className="remove-label-tests-modal__removal-item">
										<EvTag status={TAG_STATUS.INFO} text={removal.label.label} />
										<RemoveIconButton
											onClick={() => handleRemoveRemoval(index, removal.label.label)}
											ariaLabel={`Remove from removal list: ${removal.label.label}`}
											className="remove-label-tests-modal__removal-remove-btn"
										/>
									</div>
								))}
							</div>
						</div>
					</>
				)}
			</div>
		</EvPopup>
	);
};
